import React, { useState, useEffect, useCallback } from 'react';
import { propertyAPI, unitAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Building2, Plus, Grid3X3, List, Eye, Edit, Trash2, Clock, 
  AlertTriangle, CheckCircle, XCircle, Home, Settings, RefreshCw,
  ChevronLeft, ChevronRight, History, User, Calendar
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG = {
  available: { label: 'Available', color: 'bg-emerald-500', textColor: 'text-emerald-500', bgLight: 'bg-emerald-50 border-emerald-200' },
  reserved: { label: 'Reserved', color: 'bg-amber-500', textColor: 'text-amber-500', bgLight: 'bg-amber-50 border-amber-200' },
  booked: { label: 'Booked', color: 'bg-blue-500', textColor: 'text-blue-500', bgLight: 'bg-blue-50 border-blue-200' },
  spa_signed: { label: 'SPA Signed', color: 'bg-purple-500', textColor: 'text-purple-500', bgLight: 'bg-purple-50 border-purple-200' },
  sold: { label: 'Sold', color: 'bg-slate-500', textColor: 'text-slate-400', bgLight: 'bg-slate-100 border-slate-300' },
};

const UnitAvailability = () => {
  // State
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitDetailsOpen, setUnitDetailsOpen] = useState(false);
  
  // Modals
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [addUnitModalOpen, setAddUnitModalOpen] = useState(false);
  const [editUnitModalOpen, setEditUnitModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [bulkGenerateModalOpen, setBulkGenerateModalOpen] = useState(false);
  
  // Form states
  const [unitForm, setUnitForm] = useState({
    unit_number: '',
    floor: '',
    block: '',
    bedrooms: '',
    bathrooms: '',
    size_sqft: '',
    price: '',
    unit_type: '',
  });
  
  const [bulkConfig, setBulkConfig] = useState({
    block: 'A',
    startFloor: 1,
    endFloor: 10,
    unitsPerFloor: 8,
    unitTypes: '1BR, 2BR, 3BR',
    basePrice: 500000,
    pricePerFloor: 10000,
  });

  // Load data
  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      loadUnits();
      // Set up polling for real-time updates
      const interval = setInterval(loadUnits, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [selectedProperty]);

  const loadProperties = async () => {
    try {
      const res = await propertyAPI.getProperties();
      // V4.18.44 — Unit Availability manages inventory only for SAM Rumah
      // exclusive projects. Non-exclusive listings don't have a
      // system-managed unit inventory, so hide them from this module.
      const list = (res.data || []).filter(p => p.is_exclusive === true || p.is_exclusive === 'true');
      setProperties(list);
      if (list.length > 0) {
        setSelectedProperty(list[0]);
      }
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const loadUnits = async () => {
    if (!selectedProperty) return;
    try {
      const res = await propertyAPI.getAvailability(selectedProperty.id);
      setUnits(res.data.units || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error('Error loading units:', error);
    }
  };

  // Get unique floors and blocks (filter out null/undefined to avoid crashes on legacy data)
  const floors = [...new Set(units.map(u => u.floor).filter(f => f !== null && f !== undefined))].sort((a, b) => b - a);
  const blocks = [...new Set(units.map(u => u.block || 'A').filter(Boolean))].sort();

  // Filter units
  const filteredUnits = units.filter(unit => {
    if (selectedFloor !== 'all' && unit.floor !== parseInt(selectedFloor)) return false;
    if (selectedStatus !== 'all' && unit.status !== selectedStatus) return false;
    return true;
  });

  // Group units by floor for grid view
  const unitsByFloor = floors.reduce((acc, floor) => {
    acc[floor] = filteredUnits.filter(u => u.floor === floor).sort((a, b) => 
      a.unit_number.localeCompare(b.unit_number)
    );
    return acc;
  }, {});

  // Handlers
  const handleCreateUnit = async () => {
    // V4.18.44 — Price is mandatory for unit inventory. Validate before submit.
    const priceNum = parseFloat(unitForm.price);
    if (!unitForm.price || isNaN(priceNum) || priceNum <= 0) {
      toast.error('Price (RM) is required and must be greater than 0');
      return;
    }
    try {
      await unitAPI.createUnit({
        property_id: selectedProperty.id,
        ...unitForm,
        floor: parseInt(unitForm.floor),
        bedrooms: parseInt(unitForm.bedrooms) || 0,
        bathrooms: parseInt(unitForm.bathrooms) || 0,
        size_sqft: parseFloat(unitForm.size_sqft) || 0,
        price: priceNum,
      });
      toast.success('Unit created successfully');
      setAddUnitModalOpen(false);
      resetUnitForm();
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create unit');
    }
  };

  const handleUpdateUnit = async () => {
    // V4.18.44 — Price is mandatory on update too.
    const priceNum = parseFloat(unitForm.price);
    if (!unitForm.price || isNaN(priceNum) || priceNum <= 0) {
      toast.error('Price (RM) is required and must be greater than 0');
      return;
    }
    try {
      await unitAPI.updateUnit(selectedUnit.id, {
        property_id: selectedProperty.id,
        ...unitForm,
        floor: parseInt(unitForm.floor),
        bedrooms: parseInt(unitForm.bedrooms) || 0,
        bathrooms: parseInt(unitForm.bathrooms) || 0,
        size_sqft: parseFloat(unitForm.size_sqft) || 0,
        price: priceNum,
      });
      toast.success('Unit updated successfully');
      setEditUnitModalOpen(false);
      resetUnitForm();
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update unit');
    }
  };

  const handleDeleteUnit = async () => {
    try {
      await unitAPI.deleteUnit(unitToDelete.id);
      toast.success('Unit deleted successfully');
      setDeleteDialogOpen(false);
      setUnitToDelete(null);
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete unit');
    }
  };

  const handleStatusChange = async (unitId, newStatus) => {
    try {
      await unitAPI.updateStatus(unitId, newStatus);
      toast.success('Status updated');
      loadUnits();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleBulkGenerate = async () => {
    try {
      const unitsToCreate = [];
      const unitTypes = bulkConfig.unitTypes.split(',').map(t => t.trim());
      
      for (let floor = bulkConfig.startFloor; floor <= bulkConfig.endFloor; floor++) {
        for (let unit = 1; unit <= bulkConfig.unitsPerFloor; unit++) {
          const unitNumber = `${bulkConfig.block}-${floor.toString().padStart(2, '0')}-${unit.toString().padStart(2, '0')}`;
          const unitType = unitTypes[(unit - 1) % unitTypes.length];
          const price = bulkConfig.basePrice + ((floor - 1) * bulkConfig.pricePerFloor);
          
          unitsToCreate.push({
            property_id: selectedProperty.id,
            unit_number: unitNumber,
            floor: floor,
            block: bulkConfig.block,
            unit_type: unitType,
            price: price,
            status: 'available',
          });
        }
      }
      
      await unitAPI.bulkCreate(unitsToCreate);
      toast.success(`${unitsToCreate.length} units created successfully`);
      setBulkGenerateModalOpen(false);
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate units');
    }
  };

  const resetUnitForm = () => {
    setUnitForm({
      unit_number: '',
      floor: '',
      block: '',
      bedrooms: '',
      bathrooms: '',
      size_sqft: '',
      price: '',
      unit_type: '',
    });
  };

  const openEditModal = (unit) => {
    setSelectedUnit(unit);
    setUnitForm({
      unit_number: unit.unit_number,
      floor: unit.floor?.toString() || '',
      block: unit.block || '',
      bedrooms: unit.bedrooms?.toString() || '',
      bathrooms: unit.bathrooms?.toString() || '',
      size_sqft: unit.size_sqft?.toString() || '',
      price: unit.price?.toString() || '',
      unit_type: unit.unit_type || '',
    });
    setEditUnitModalOpen(true);
  };

  const openUnitDetails = (unit) => {
    setSelectedUnit(unit);
    setUnitDetailsOpen(true);
  };

  const confirmDelete = (unit) => {
    setUnitToDelete(unit);
    setDeleteDialogOpen(true);
  };

  // Check for expiring reservations
  const expiringReservations = units.filter(u => {
    if (u.status !== 'reserved' || !u.reserved_at) return false;
    const reservedTime = new Date(u.reserved_at);
    const now = new Date();
    const minutesElapsed = (now - reservedTime) / (1000 * 60);
    return minutesElapsed >= 25; // Alert at 25 minutes (5 min before 30 min deadline)
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="unit-availability-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-7 w-7 text-purple-600" />
            Unit Availability
          </h1>
          <p className="text-gray-500 mt-1">Manage project units and real-time availability</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadUnits} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => setBulkGenerateModalOpen(true)} disabled={!selectedProperty} data-testid="bulk-generate-btn">
            <Grid3X3 className="w-4 h-4 mr-2" />
            Bulk Generate
          </Button>
          <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => setAddUnitModalOpen(true)} disabled={!selectedProperty} data-testid="add-unit-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Unit
          </Button>
        </div>
      </div>

      {/* Expiring Reservations Alert */}
      {expiringReservations.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">
                {expiringReservations.length} reservation(s) expiring soon!
              </p>
              <p className="text-sm text-amber-600">
                Units: {expiringReservations.map(u => u.unit_number).join(', ')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project Selector & Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-gray-600 text-sm">Project</Label>
              <Select 
                value={selectedProperty?.id || ''} 
                onValueChange={(id) => setSelectedProperty(properties.find(p => p.id === id))}
              >
                <SelectTrigger className="mt-1" data-testid="project-selector">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-32">
              <Label className="text-gray-600 text-sm">Floor</Label>
              <Select value={selectedFloor} onValueChange={setSelectedFloor}>
                <SelectTrigger className="mt-1" data-testid="floor-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Floors</SelectItem>
                  {floors.map((floor) => (
                    <SelectItem key={floor} value={floor.toString()}>
                      Floor {floor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-36">
              <Label className="text-gray-600 text-sm">Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="mt-1" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-purple-600' : ''}
                data-testid="grid-view-btn"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className={viewMode === 'table' ? 'bg-purple-600' : ''}
                data-testid="table-view-btn"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {selectedProperty && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-900">{stats.total || 0}</p>
              <p className="text-sm text-gray-500">Total Units</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{stats.available || 0}</p>
              <p className="text-sm text-emerald-700">Available</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{stats.reserved || 0}</p>
              <p className="text-sm text-amber-700">Reserved</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{stats.booked || 0}</p>
              <p className="text-sm text-blue-700">Booked</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-600">{stats.sold || 0}</p>
              <p className="text-sm text-slate-700">Sold</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Status Legend */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-gray-500">Status:</span>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <div key={key} className="flex items-center gap-1">
            <div className={`w-3 h-3 rounded ${config.color}`}></div>
            <span className="text-gray-600">{config.label}</span>
          </div>
        ))}
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && selectedProperty && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Floor Plan - {selectedProperty.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {floors.length > 0 ? (
              <div className="space-y-4">
                {floors.map((floor) => (
                  <div key={floor} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline" className="font-semibold">
                        Floor {floor}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        ({unitsByFloor[floor]?.length || 0} units)
                      </span>
                    </div>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2">
                      {(unitsByFloor[floor] || []).map((unit) => {
                        const statusConfig = STATUS_CONFIG[unit.status] || STATUS_CONFIG.available;
                        const isExpiring = unit.status === 'reserved' && unit.reserved_at && 
                          ((new Date() - new Date(unit.reserved_at)) / (1000 * 60)) >= 25;
                        
                        return (
                          <button
                            key={unit.id}
                            onClick={() => openUnitDetails(unit)}
                            className={`
                              relative p-2 rounded-lg border-2 transition-all hover:scale-105 hover:shadow-md
                              ${statusConfig.bgLight}
                              ${isExpiring ? 'ring-2 ring-amber-400 ring-offset-1' : ''}
                            `}
                            data-testid={`unit-${unit.id}`}
                          >
                            <div className={`w-3 h-3 rounded-full ${statusConfig.color} absolute top-1 right-1`}></div>
                            <p className="text-xs font-semibold text-gray-800 truncate">
                              {unit.unit_number.split('-').pop()}
                            </p>
                            <p className="text-[10px] text-gray-500">{unit.unit_type}</p>
                            {isExpiring && (
                              <Clock className="w-3 h-3 text-amber-500 absolute bottom-1 right-1" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No Units Found</h3>
                <p className="text-gray-400 mb-4">Add units to this property or use bulk generate</p>
                <Button onClick={() => setBulkGenerateModalOpen(true)}>
                  <Grid3X3 className="w-4 h-4 mr-2" />
                  Bulk Generate Units
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table View */}
      {viewMode === 'table' && selectedProperty && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Unit</TableHead>
                  <TableHead>Block</TableHead>
                  <TableHead>Floor</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reserved By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUnits.length > 0 ? (
                  filteredUnits.map((unit) => {
                    const statusConfig = STATUS_CONFIG[unit.status] || STATUS_CONFIG.available;
                    const isExpiring = unit.status === 'reserved' && unit.reserved_at && 
                      ((new Date() - new Date(unit.reserved_at)) / (1000 * 60)) >= 25;
                    
                    return (
                      <TableRow key={unit.id} className={isExpiring ? 'bg-amber-50' : ''} data-testid={`unit-row-${unit.id}`}>
                        <TableCell className="font-medium">{unit.unit_number}</TableCell>
                        <TableCell>{unit.block || '-'}</TableCell>
                        <TableCell>{unit.floor}</TableCell>
                        <TableCell>{unit.unit_type || '-'}</TableCell>
                        <TableCell>{unit.size_sqft ? `${unit.size_sqft} sqft` : '-'}</TableCell>
                        <TableCell className="font-mono text-emerald-600">
                          {unit.price ? formatCurrency(unit.price) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusConfig.bgLight} ${statusConfig.textColor} border`}>
                            {statusConfig.label}
                            {isExpiring && <Clock className="w-3 h-3 ml-1 inline" />}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {unit.reserved_by_name || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openUnitDetails(unit)} data-testid={`view-${unit.id}`}>
                              <Eye className="w-4 h-4 text-gray-500" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditModal(unit)} data-testid={`edit-${unit.id}`}>
                              <Edit className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => confirmDelete(unit)} data-testid={`delete-${unit.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-gray-500">
                      No units found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Unit Details Sheet */}
      <Sheet open={unitDetailsOpen} onOpenChange={setUnitDetailsOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedUnit && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Home className="h-5 w-5 text-purple-600" />
                  Unit {selectedUnit.unit_number}
                </SheetTitle>
                <SheetDescription>
                  {selectedProperty?.name} - Floor {selectedUnit.floor}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <Badge className={`${STATUS_CONFIG[selectedUnit.status]?.bgLight} ${STATUS_CONFIG[selectedUnit.status]?.textColor} border px-3 py-1`}>
                    {STATUS_CONFIG[selectedUnit.status]?.label}
                  </Badge>
                  <Select value={selectedUnit.status} onValueChange={(s) => handleStatusChange(selectedUnit.id, s)}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                {/* Unit Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Unit Number</p>
                    <p className="font-medium">{selectedUnit.unit_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Block</p>
                    <p className="font-medium">{selectedUnit.block || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Floor</p>
                    <p className="font-medium">{selectedUnit.floor}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Type</p>
                    <p className="font-medium">{selectedUnit.unit_type || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Size</p>
                    <p className="font-medium">{selectedUnit.size_sqft ? `${selectedUnit.size_sqft} sqft` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Bedrooms</p>
                    <p className="font-medium">{selectedUnit.bedrooms || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Bathrooms</p>
                    <p className="font-medium">{selectedUnit.bathrooms || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Price</p>
                    <p className="font-medium text-emerald-600">{selectedUnit.price ? formatCurrency(selectedUnit.price) : '-'}</p>
                  </div>
                </div>

                {/* Reservation Info */}
                {selectedUnit.status === 'reserved' && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Reservation Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-amber-700">Reserved By:</span>
                          <span className="font-medium">{selectedUnit.reserved_by_name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-amber-700">Reserved At:</span>
                          <span className="font-medium">{selectedUnit.reserved_at ? formatDate(selectedUnit.reserved_at) : 'N/A'}</span>
                        </div>
                        {selectedUnit.reserved_at && (
                          <div className="flex justify-between">
                            <span className="text-amber-700">Time Remaining:</span>
                            <span className="font-medium">
                              {Math.max(0, 30 - Math.floor((new Date() - new Date(selectedUnit.reserved_at)) / (1000 * 60)))} minutes
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Booking Info */}
                {(selectedUnit.status === 'booked' || selectedUnit.status === 'sold') && selectedUnit.booking_id && (
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Booking Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-700">Customer:</span>
                          <span className="font-medium">{selectedUnit.customer_name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Agent:</span>
                          <span className="font-medium">{selectedUnit.booked_by_name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Booked At:</span>
                          <span className="font-medium">{selectedUnit.booked_at ? formatDate(selectedUnit.booked_at) : 'N/A'}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* History */}
                {selectedUnit.history && selectedUnit.history.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Status History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedUnit.history.slice(0, 5).map((entry, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm border-b pb-2">
                            <div>
                              <Badge variant="outline" className="text-xs">
                                {STATUS_CONFIG[entry.status]?.label || entry.status}
                              </Badge>
                              <span className="text-gray-500 ml-2">{entry.changed_by_name}</span>
                            </div>
                            <span className="text-gray-400 text-xs">{formatDate(entry.changed_at)}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className="flex-1" onClick={() => openEditModal(selectedUnit)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button variant="outline" className="flex-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => confirmDelete(selectedUnit)}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Unit Modal */}
      <Dialog open={addUnitModalOpen} onOpenChange={setAddUnitModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Unit</DialogTitle>
            <DialogDescription>Add a unit to {selectedProperty?.name}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Unit Number *</Label>
              <Input
                value={unitForm.unit_number}
                onChange={(e) => setUnitForm({ ...unitForm, unit_number: e.target.value })}
                placeholder="e.g., A-12-01"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Block</Label>
              <Input
                value={unitForm.block}
                onChange={(e) => setUnitForm({ ...unitForm, block: e.target.value })}
                placeholder="e.g., A"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Floor *</Label>
              <Input
                type="number"
                value={unitForm.floor}
                onChange={(e) => setUnitForm({ ...unitForm, floor: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Unit Type</Label>
              <Input
                value={unitForm.unit_type}
                onChange={(e) => setUnitForm({ ...unitForm, unit_type: e.target.value })}
                placeholder="e.g., 2BR"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Bedrooms</Label>
              <Input
                type="number"
                value={unitForm.bedrooms}
                onChange={(e) => setUnitForm({ ...unitForm, bedrooms: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Bathrooms</Label>
              <Input
                type="number"
                value={unitForm.bathrooms}
                onChange={(e) => setUnitForm({ ...unitForm, bathrooms: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Size (sqft)</Label>
              <Input
                type="number"
                value={unitForm.size_sqft}
                onChange={(e) => setUnitForm({ ...unitForm, size_sqft: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Price (RM) *</Label>
              <Input
                type="number"
                value={unitForm.price}
                onChange={(e) => setUnitForm({ ...unitForm, price: e.target.value })}
                className="mt-1"
                required
                min="0.01"
                step="0.01"
                data-testid="unit-price-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUnitModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateUnit} className="bg-purple-600 hover:bg-purple-700">Create Unit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Modal */}
      <Dialog open={editUnitModalOpen} onOpenChange={setEditUnitModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Unit</DialogTitle>
            <DialogDescription>Update unit details</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Unit Number *</Label>
              <Input
                value={unitForm.unit_number}
                onChange={(e) => setUnitForm({ ...unitForm, unit_number: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Block</Label>
              <Input
                value={unitForm.block}
                onChange={(e) => setUnitForm({ ...unitForm, block: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Floor *</Label>
              <Input
                type="number"
                value={unitForm.floor}
                onChange={(e) => setUnitForm({ ...unitForm, floor: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Unit Type</Label>
              <Input
                value={unitForm.unit_type}
                onChange={(e) => setUnitForm({ ...unitForm, unit_type: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Bedrooms</Label>
              <Input
                type="number"
                value={unitForm.bedrooms}
                onChange={(e) => setUnitForm({ ...unitForm, bedrooms: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Bathrooms</Label>
              <Input
                type="number"
                value={unitForm.bathrooms}
                onChange={(e) => setUnitForm({ ...unitForm, bathrooms: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Size (sqft)</Label>
              <Input
                type="number"
                value={unitForm.size_sqft}
                onChange={(e) => setUnitForm({ ...unitForm, size_sqft: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Price (RM) *</Label>
              <Input
                type="number"
                value={unitForm.price}
                onChange={(e) => setUnitForm({ ...unitForm, price: e.target.value })}
                className="mt-1"
                required
                min="0.01"
                step="0.01"
                data-testid="unit-price-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUnitModalOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateUnit} className="bg-purple-600 hover:bg-purple-700">Update Unit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Generate Modal */}
      <Dialog open={bulkGenerateModalOpen} onOpenChange={setBulkGenerateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Generate Units</DialogTitle>
            <DialogDescription>
              Automatically generate units for {selectedProperty?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Block/Tower</Label>
              <Input
                value={bulkConfig.block}
                onChange={(e) => setBulkConfig({ ...bulkConfig, block: e.target.value })}
                placeholder="e.g., A, B, Tower 1"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Floor</Label>
                <Input
                  type="number"
                  value={bulkConfig.startFloor}
                  onChange={(e) => setBulkConfig({ ...bulkConfig, startFloor: parseInt(e.target.value) || 1 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Floor</Label>
                <Input
                  type="number"
                  value={bulkConfig.endFloor}
                  onChange={(e) => setBulkConfig({ ...bulkConfig, endFloor: parseInt(e.target.value) || 10 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Units Per Floor</Label>
              <Input
                type="number"
                value={bulkConfig.unitsPerFloor}
                onChange={(e) => setBulkConfig({ ...bulkConfig, unitsPerFloor: parseInt(e.target.value) || 8 })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Unit Types (comma separated)</Label>
              <Input
                value={bulkConfig.unitTypes}
                onChange={(e) => setBulkConfig({ ...bulkConfig, unitTypes: e.target.value })}
                placeholder="e.g., 1BR, 2BR, 3BR"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Base Price (RM)</Label>
                <Input
                  type="number"
                  value={bulkConfig.basePrice}
                  onChange={(e) => setBulkConfig({ ...bulkConfig, basePrice: parseInt(e.target.value) || 500000 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Price/Floor (RM)</Label>
                <Input
                  type="number"
                  value={bulkConfig.pricePerFloor}
                  onChange={(e) => setBulkConfig({ ...bulkConfig, pricePerFloor: parseInt(e.target.value) || 10000 })}
                  className="mt-1"
                />
              </div>
            </div>
            <Card className="bg-gray-50">
              <CardContent className="p-3 text-sm">
                <p className="font-medium text-gray-700">Preview:</p>
                <p className="text-gray-600">
                  {(bulkConfig.endFloor - bulkConfig.startFloor + 1) * bulkConfig.unitsPerFloor} units will be created
                </p>
                <p className="text-gray-500">
                  Format: {bulkConfig.block}-01-01 to {bulkConfig.block}-{bulkConfig.endFloor.toString().padStart(2, '0')}-{bulkConfig.unitsPerFloor.toString().padStart(2, '0')}
                </p>
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkGenerateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkGenerate} className="bg-purple-600 hover:bg-purple-700">
              Generate Units
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Unit
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete unit <strong>{unitToDelete?.unit_number}</strong>?
              <br /><br />
              <span className="text-amber-600">
                Note: The unit history will be preserved in the audit log.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUnit} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UnitAvailability;
